import React, { useEffect, useState } from 'react'
import { Badge, Button, ListGroup, OverlayTrigger, Popover } from 'react-bootstrap'
import { Link } from 'react-router-dom'

import moment from 'moment'

import { NotificationService } from '../../services/NotificationService'
import FAIcon from '../FAIcon'

import NotificationMessageTray from './NotificationMessageTray'

import './NotificationList.scss'

const NotificationList = ({ currentUser }) => {
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [showPopover, setShowPopover] = useState(false)
  const [showTray, setShowTray] = useState(false)
  const [selectedMessage, setSelectedMessage] = useState(null)

  const safeFromNow = dateStr => {
    try {
      if (!dateStr) return ''
      const m = moment.utc(dateStr)
      if (!m.isValid()) return 'Invalid Date'
      return m.local().fromNow()
    } catch (e) {
      console.error('Date parsing error', e)
      return ''
    }
  }

  const fetchNotifications = async () => {
    // If popover is open or tray is open, we might want to be careful about refreshing list to avoid jumping content
    // But for badge sync, it's fine.
    // Ideally we silent-update the list or just the count.

    // For now, simple re-fetch
    setLoading(prev => (notifications.length === 0 ? true : prev)) // Only show loading spinner on initial empty state
    try {
      const [listRes, countRes] = await Promise.all([
        NotificationService.getNotifications(1, 5),
        NotificationService.getUnreadCount(),
      ])
      setNotifications(listRes.results || [])
      setUnreadCount(countRes || 0)
    } catch (error) {
      console.error('Failed to fetch notifications', error)
    } finally {
      setLoading(false)
    }
  }

  // Poll on mount, and whenever the redux count changes (trigger from SSE)
  useEffect(() => {
    fetchNotifications()
  }, [currentUser?.unreadNotificationsCount])

  const handleToggle = nextShow => {
    setShowPopover(nextShow)
    if (nextShow) {
      fetchNotifications()
    }
  }

  const handleMarkAsRead = async (id, e) => {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    // Optimistic update
    setNotifications(prev => prev.map(n => (n.id === id ? { ...n, isRead: true } : n)))
    setUnreadCount(prev => Math.max(0, prev - 1))

    try {
      await NotificationService.markAsRead(id)
    } catch (error) {
      console.error('Failed to mark read', error)
    }
  }

  const handleMarkAllRead = async () => {
    // Optimistic update
    const previousNotifications = notifications
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })))
    setUnreadCount(0)

    try {
      await NotificationService.markAllAsRead()
    } catch (error) {
      console.error('Failed to mark all read', error)
      // Revert
      setNotifications(previousNotifications)
      setUnreadCount(previousNotifications.filter(n => !n.isRead).length)
    }
  }

  const handleMessageClick = (n, e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    // Mark as read immediately when opening
    if (!n.isRead) {
      handleMarkAsRead(n.id)
    }

    setSelectedMessage(n)
    setShowTray(true)
    setShowPopover(false)
  }

  const popover = (
    <Popover id="notification-popover" className="notification-popover">
      <Popover.Content className="p-0">
        <div className="d-flex justify-content-between align-items-center p-2 border-bottom">
          <h6 className="mb-0">Notifications</h6>
          {unreadCount > 0 && (
            <Button variant="link" size="sm" className="p-0 text-muted" onClick={handleMarkAllRead}>
              Mark all read
            </Button>
          )}
        </div>
        <ListGroup variant="flush" className="notification-list-group">
          {loading ? (
            <div className="p-3 text-center text-muted">Loading...</div>
          ) : notifications.length === 0 ? (
            <div className="p-3 text-center text-muted">No notifications</div>
          ) : (
            notifications.map(n => (
              <ListGroup.Item
                key={n.id}
                action
                className={`notification-item ${!n.isRead ? 'bg-light' : ''}`}
                onClick={e => handleMessageClick(n, e)}
              >
                <div className="d-flex justify-content-between w-100">
                  <strong className="text-truncate mr-2" title={n.title}>
                    {n.title}
                  </strong>
                  <small className="text-muted text-nowrap">
                    {/* Guard Clause for Moment.js */}
                    {safeFromNow(n.createdUtc)}
                  </small>
                </div>
                {n.senderName && <div className="small text-muted text-truncate mb-1">From: {n.senderName}</div>}
                <div className="small text-muted text-truncate" title={n.body}>
                  {n.body}
                </div>
                {!n.isRead && (
                  <div className="text-right mt-1">
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
                    <small className="text-primary" onClick={e => handleMarkAsRead(n.id, e)}>
                      Mark read
                    </small>
                  </div>
                )}
              </ListGroup.Item>
            ))
          )}
        </ListGroup>
        <div className="border-top text-center py-2">
          <Link to="/notifications" className="small text-primary" onClick={() => setShowPopover(false)}>
            See all notifications →
          </Link>
        </div>
      </Popover.Content>
    </Popover>
  )

  return (
    <>
      <OverlayTrigger
        trigger="click"
        placement="bottom"
        overlay={popover}
        rootClose
        show={showPopover}
        onToggle={handleToggle}
      >
        <div className="nav-link position-relative cursor-pointer" role="button" title="General Notifications">
          <FAIcon name="bell" />
          {unreadCount > 0 && (
            <Badge pill variant="danger" className="notification-badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </div>
      </OverlayTrigger>

      <NotificationMessageTray isOpen={showTray} onClose={() => setShowTray(false)} message={selectedMessage} />
    </>
  )
}

export default NotificationList
